import { ISetting } from '@shared/interfaces/setting.interface';

/** A recipient column of the matrix: the `send_to_*` flag it toggles. */
export type NotificationMatrixColumn =
  | 'send_to_assigned'
  | 'send_to_requested'
  | 'send_to_root'
  | 'send_to_custom';

export const NOTIFICATION_MATRIX_COLUMNS: NotificationMatrixColumn[] = [
  'send_to_assigned',
  'send_to_requested',
  'send_to_root',
  'send_to_custom'
];

export interface INotificationMatrixRow {
  /** `CustomNotification.notification_type` on the API. */
  type: string;
  /** `appSettingAnchor` of the row; also its settings-search id and URL fragment. */
  anchorId: string;
  /** `SETTINGS.CONFIGURATION.TEAM_NOTIFICATIONS.ROWS.<key>` i18n block. */
  key: string;
  /** Mirrors the feature the event belongs to; hidden rows are neither rendered nor searchable. */
  isVisible: (settings?: ISetting) => boolean;
}

const always = (): boolean => true;
const hasNewLeadSystem = (settings?: ISetting): boolean =>
  !!(
    settings?.settings_json?.lead_generation_setting?.enabled ||
    settings?.settings_json?.lead_generation?.enabled
  );

/**
 * Single source of truth for the rows of the "Team notifications" matrix, in
 * display order. The matrix component renders it and the settings-search
 * registry derives its entries from it, so an anchor can never go missing.
 */
export const NOTIFICATION_MATRIX_ROWS: INotificationMatrixRow[] = [
  {
    type: 'reference_completed',
    anchorId: 'team_notifications_reference_completed',
    key: 'REFERENCE_COMPLETED',
    isVisible: always
  },
  {
    type: 'all_references_completed',
    anchorId: 'team_notifications_all_references_completed',
    key: 'ALL_REFERENCES_COMPLETED',
    isVisible: always
  },
  {
    type: 'reference_request_completed',
    anchorId: 'team_notifications_reference_request_completed',
    key: 'REFERENCE_REQUEST_COMPLETED',
    isVisible: always
  },
  {
    type: 'reference_pending_review',
    anchorId: 'team_notifications_reference_pending_review',
    key: 'REFERENCE_PENDING_REVIEW',
    isVisible: (settings?: ISetting) => !!settings?.review_candidate_references
  },
  {
    type: 'unresponsive',
    anchorId: 'team_notifications_unresponsive',
    key: 'UNRESPONSIVE',
    isVisible: always
  },
  {
    type: 'email_delivery_failed',
    anchorId: 'team_notifications_email_delivery_failed',
    key: 'EMAIL_DELIVERY_FAILED',
    isVisible: always
  },
  {
    type: 'text_delivery_failed',
    anchorId: 'team_notifications_text_delivery_failed',
    key: 'TEXT_DELIVERY_FAILED',
    isVisible: (settings?: ISetting) => !!settings?.is_text_messages_enabled
  },
  {
    type: 'ip_conflict',
    anchorId: 'team_notifications_ip_conflict',
    key: 'IP_CONFLICT',
    isVisible: always
  },
  {
    type: 'feedbacks',
    anchorId: 'team_notifications_feedbacks',
    key: 'FEEDBACKS',
    isVisible: (settings?: ISetting) =>
      !!settings?.is_plugins_candidate_feedback_enabled ||
      !!settings?.is_plugins_recruiter_feedback_enabled
  },
  {
    type: 'pre_screening',
    anchorId: 'team_notifications_pre_screening',
    key: 'PRE_SCREENING',
    isVisible: (settings?: ISetting) =>
      !!settings?.is_plugins_pre_screening_enabled
  },
  {
    type: 'leads',
    anchorId: 'team_notifications_leads',
    key: 'LEADS',
    isVisible: (settings?: ISetting) => !hasNewLeadSystem(settings)
  }
];

/** Legacy hiring-leads firms keep a per-address list instead of plain custom emails. */
export const isLegacyHiringLeads = (
  type: string,
  settings?: ISetting
): boolean =>
  type === 'leads' &&
  settings?.thank_you_cta === true &&
  settings?.lead_generation_type === 'hiring';
